/** One device: newest values, a chart of any reading, its events and command history. View only for now. */
import { useEffect, useState } from "react";
import { api, ApiError, type Device } from "../api.ts";
import { LineChart } from "../chart.tsx";
import {
  ago,
  arrangeReadings,
  clock,
  COMMAND_INFO,
  deviceStatus,
  EVENT_INFO,
  formatEui,
  formatNumber,
  portKey,
  portLabel,
  tankLevel,
  tankPercent,
  readsDistance,
  formatReading,
  isFmsBoard,
  modelName,
  STATUS_TEXT,
  STATUS_TONE,
  type ShownReading,
} from "../format.ts";
import { go } from "../route.ts";
import { Bars, Icon, Problem, SavedCopyBanner, Section, Spinner, useLoad, useNow } from "../ui.tsx";
import { loadDevice } from "../farm-data.ts";
import { DeviceNotes } from "./device-notes.tsx";
import { can } from "../control.ts";
import { OutputsCard } from "./control.tsx";
import { BoardRulesCard, BoardRulesOffBanner } from "./board-rules.tsx";
import { BoardModbusCard } from "./board-modbus.tsx";
import { FlowMeter, Gauge, MoistureBar, SOIL_COLOURS, SoilChart, Sparkline, StatusLight, Tank, Thermometer, WindDial, WindRose } from "../widgets.tsx";
import { daysToRefill, rainRatePort, riseSince, soilBand, soilPorts, soilSummary, widgetFor, widgetRange, windPorts, type Widget } from "../../../../packages/core/src/ports.ts";
import { InsightList } from "./ai.tsx";
import { useHomePins } from "../home-pins.tsx";

const HOUR = 3_600_000;
const RANGES = [
  { id: "24h", label: "24 h", ms: 24 * HOUR },
  { id: "7d", label: "7 days", ms: 7 * 24 * HOUR },
  { id: "30d", label: "30 days", ms: 30 * 24 * HOUR },
] as const;

type HistoryPoint = { at: number; value: number; raw?: number; unit?: string };

/**
 * A port's history, oldest first. Up to a day: every reading. Longer: GET /chart's hourly
 * points (a fortnight of 5-minute soil readings is 4,000 rows per depth, too much for a phone
 * on one bar), with on/off ports as just their changes. `counter`: a running total (rain gauge)
 * is taken at each hour's highest value, placed just before the hour's end (/chart puts hourly
 * points mid-hour), so rises between points add up to what fell and "since midnight" starts
 * at the 11 pm hour's total. Falls back to raw readings if /chart leaves the port out.
 */
async function portHistory(device: Device, port: string, from: number, to: number, opts: { counter?: boolean } = {}): Promise<{ points: HistoryPoint[]; detail: "raw" | "hour" | "changes" }> {
  if (to - from > 25 * HOUR) {
    const r = await api.chart([`${device.devEui}:${port}`], from, to, device.accountId);
    const s = r.series[0];
    if (s) {
      const unit = s.unit ? { unit: s.unit } : {};
      const points = s.points.map((p) =>
        opts.counter && s.detail === "hour" ? { at: p.at + HOUR / 2 - 1, value: p.max ?? p.value, ...unit } : { at: p.at, value: p.value, ...unit },
      );
      return { points, detail: s.detail };
    }
  }
  const r = await api.readings(device.devEui, port, from, to);
  return { points: [...r.readings].sort((a, b) => a.at - b.at), detail: "raw" };
}

export function DevicePage({ eui, siteName, role, myId }: { eui: string; siteName: (id: string) => string | undefined; role: string; myId: string }) {
  const now = useNow();
  const home = useHomePins();
  const latest = useLoad(() => loadDevice(eui), [eui], 30_000);
  const events = useLoad(() => api.events(eui, 30), [eui], 60_000);
  const commands = useLoad(() => api.commands(eui, 15), [eui], 60_000);
  const [port, setPort] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<unknown>();

  const device = latest.data?.device;
  const arranged = device ? arrangeReadings(device, latest.data!.readings) : { measured: [], health: [] };
  // Chart the first measured value until the user picks another.
  useEffect(() => {
    if (!port && arranged.measured[0]) setPort(arranged.measured[0].port);
  }, [port, arranged.measured]);

  if (latest.error && !device) {
    if (latest.error instanceof ApiError && latest.error.code === "not-found") {
      return (
        <div className="card empty">
          This device isn't on your account.{" "}
          <button className="btn ghost" onClick={() => go({ name: "devices" })}>
            Back to devices
          </button>
        </div>
      );
    }
    return <Problem error={latest.error} onRetry={latest.reload} />;
  }
  if (!device) return <Spinner />;

  // No signal: the readings are a copy saved on the phone, or the last ones this page got. The
  // device may have changed since, so the switches are off until the server answers again.
  const savedAt = latest.data!.savedAt;
  // Any failed refresh counts (a server restarting answers 502 rather than nothing at all).
  const noSignal = savedAt !== undefined || latest.error !== undefined;
  const status = deviceStatus(device, now);
  const rssi = latest.data!.readings.find((r) => r.port === "rssi")?.value;
  const all = [...arranged.measured, ...arranged.health];
  const selected = all.find((r) => r.port === port);

  return (
    <>
      <div className="dev-head">
        <button className="icon-btn" onClick={() => (history.length > 1 ? history.back() : go({ name: "devices" }))} aria-label="Back">
          <Icon name="back" />
        </button>
        <div style={{ minWidth: 0, flex: "1 1 200px" }}>
          <h1>{device.name}</h1>
          <div className="muted small">
            {modelName(device.model)}
            {siteName(device.siteId) ? ` · ${siteName(device.siteId)}` : ""}
          </div>
        </div>
        {noSignal ? (
          <span className="pill warn">
            <span className="dot warn" /> No signal
          </span>
        ) : (
          <span className={`pill ${STATUS_TONE[status] === "muted" ? "" : STATUS_TONE[status]}`}>
            <span className={`dot ${STATUS_TONE[status]}`} /> {STATUS_TEXT[status]} · {ago(device.lastSeenAt, now)}
          </span>
        )}
        {(can(role, "setup-devices") || can(role, "edit-rules")) && (
          <button className="btn" onClick={() => go({ name: "device-setup", eui })}>
            Set up
          </button>
        )}
      </div>

      {device.archivedAt && (
        <div className="banner warn" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ flex: 1 }}>
            Archived {clock(device.archivedAt, now)}: hidden from the farm, not counted on the plan, can't be switched. Its history is kept.
          </span>
          {can(role, "setup-devices") && (
            <button
              className="btn"
              disabled={restoring}
              onClick={async () => {
                setRestoring(true);
                setRestoreError(undefined);
                try {
                  await api.restoreDevice(device.devEui);
                  latest.reload();
                } catch (e) {
                  setRestoreError(e);
                } finally {
                  setRestoring(false);
                }
              }}
            >
              {restoring ? "Bringing it back…" : "Bring it back"}
            </button>
          )}
          {restoreError ? <div style={{ flexBasis: "100%" }}><Problem error={restoreError} /></div> : null}
        </div>
      )}
      {savedAt !== undefined ? (
        <SavedCopyBanner at={savedAt} now={now} onRetry={latest.reload} />
      ) : noSignal ? (
        <div className="banner warn small" role="status" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span>
            {latest.error instanceof ApiError && latest.error.code === "offline" ? "No signal" : "Think-Control isn't answering"}: these are the last readings this page got. They'll update when it can be reached.
          </span>
          <button className="btn ghost small" onClick={latest.reload}>
            Try again
          </button>
        </div>
      ) : null}
      <BoardRulesOffBanner device={device} now={now} />
      {status === "offline" && !noSignal && !device.archivedAt && (
        <div className="banner crit">
          Nothing heard for {ago(device.lastSeenAt, now).replace(" ago", "")}. The values below are the last ones received, not live.
          {isFmsBoard(device.model) ? " Safety rules and timers stored on the board keep working without comms." : ""}
        </div>
      )}

      {soilPorts(device, latest.data!.readings.map((r) => r.port)).length > 0 && (
        <Section title="Soil moisture">
          <SoilCard device={device} ports={soilPorts(device, latest.data!.readings.map((r) => r.port))} />
        </Section>
      )}

      <Section title="Now">
        {home.error && <div className="error small">{home.error}</div>}
        {arranged.measured.length ? (
          <div className="tiles">
            {arranged.measured.map((r) => (
              <ReadingTile key={r.port} device={device} r={r} now={now} selected={r.port === port} onPick={() => setPort(r.port)} />
            ))}
          </div>
        ) : (
          <div className="card empty">No readings from this device yet.</div>
        )}
      </Section>

      <DeviceInsights devEui={device.devEui} accountId={device.accountId} />

      {!device.archivedAt && <OutputsCard device={device} readings={latest.data!.readings} role={role} now={now} noSignal={noSignal} onChanged={() => {
            latest.reload();
            commands.reload();
          }} />}
      {!device.archivedAt && <BoardRulesCard device={device} now={now} noSignal={noSignal} />}
      {!device.archivedAt && <BoardModbusCard device={device} now={now} />}

      {selected && <ChartCard device={device} reading={selected} now={now} />}

      <div className="two">
        <Section title="Activity">
          <div className="card list">
            {events.error ? <Problem error={events.error} onRetry={events.reload} /> : null}
            {!events.data && !events.error && <Spinner />}
            {events.data && !events.data.events.length && <div className="empty">Nothing to report.</div>}
            {events.data?.events.map((e, i) => {
              const info = EVENT_INFO[e.kind] ?? { label: e.kind, tone: "info" as const };
              return (
                <div className="row" key={i}>
                  <span className={`dot ${info.tone}`} />
                  <div>
                    <div className="t">{info.label}</div>
                    {e.detail && <div className="d">{e.detail}</div>}
                  </div>
                  <time dateTime={new Date(e.at).toISOString()}>{clock(e.at, now)}</time>
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Commands">
          <div className="card list">
            {commands.data && !commands.data.commands.length && <div className="empty">No commands sent from Think-Control yet.</div>}
            {!commands.data && !commands.error && <Spinner />}
            {commands.error ? <Problem error={commands.error} onRetry={commands.reload} /> : null}
            {commands.data?.commands.map((c) => {
              const info = COMMAND_INFO[c.status];
              return (
                <div className="row" key={c.id}>
                  <span className={`dot ${info.tone}`} />
                  <div>
                    <div className="t">{c.description}</div>
                    <div className="d">
                      {info.label}
                      {c.failReason ? `: ${c.failReason}` : ""} · {c.requestedBy}
                    </div>
                  </div>
                  <time dateTime={new Date(c.requestedAt).toISOString()}>{clock(c.requestedAt, now)}</time>
                </div>
              );
            })}
          </div>
          <p className="muted small" style={{ margin: 0 }}>
            Every command is shown as done only once the device confirms it.
          </p>
        </Section>
      </div>

      <Section title="Device">
        <dl className="kv">
          <div>
            <dt>DevEUI</dt>
            <dd className="mono">{formatEui(device.devEui)}</dd>
          </div>
          <div>
            <dt>Model</dt>
            <dd>{modelName(device.model)}</dd>
          </div>
          <div>
            <dt>Firmware</dt>
            <dd title={device.firmware ? undefined : "Sent when the device powers up or rejoins the network"}>{device.firmware ?? <span className="muted">Not reported yet</span>}</dd>
          </div>
          {device.info?.hardware_version !== undefined && (
            <div>
              <dt>Hardware</dt>
              <dd>{device.info.hardware_version}</dd>
            </div>
          )}
          {device.info?.serial_number !== undefined && (
            <div>
              <dt>Serial number</dt>
              <dd className="mono">{device.info.serial_number}</dd>
            </div>
          )}
          {device.info?.lorawan_class !== undefined && (
            <div>
              <dt>LoRaWAN class</dt>
              <dd>{device.info.lorawan_class}</dd>
            </div>
          )}
          {device.poweredOnAt && (
            <div>
              <dt>Last powered on</dt>
              <dd>{`${clock(device.poweredOnAt, now)} (${ago(device.poweredOnAt, now)})`}</dd>
            </div>
          )}
          <div>
            <dt>Last heard</dt>
            <dd>{device.lastSeenAt ? `${clock(device.lastSeenAt, now)} (${ago(device.lastSeenAt, now)})` : "Never"}</dd>
          </div>
          {device.gatewayEui && (
            <div>
              <dt>Gateway</dt>
              <dd>
                <GatewayName eui={device.gatewayEui} accountId={device.accountId} />
              </dd>
            </div>
          )}
          {arranged.health.map((r) => {
            const f = formatReading(device, r);
            return (
              <div key={r.port}>
                <dt>{r.label}</dt>
                <dd style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {f.value} {f.unit}
                  {r.port === "rssi" && <Bars rssi={rssi} />}
                  <button className="btn ghost" style={{ minHeight: 0, padding: "0 4px", marginLeft: "auto", fontSize: 13 }} onClick={() => setPort(r.port)}>
                    Chart
                  </button>
                </dd>
              </div>
            );
          })}
        </dl>
      </Section>

      <DeviceNotes eui={device.devEui} myId={myId} staff={role === "fms-admin"} />
    </>
  );
}

/** A reading's tile; the pin puts it on (or takes it off) the person's farm home. */
export function ReadingTile({ device, r, now, selected, onPick, footer }: { device: Device; r: ShownReading; now: number; selected?: boolean; onPick: () => void; footer?: string }) {
  const f = formatReading(device, r);
  const stale = now - r.at > 2 * HOUR;
  const cfg = device.ports.find((p) => portKey(p) === r.port);
  const widget = widgetFor(device, r.port, r.unit);
  const home = useHomePins();
  const pinned = home.isPinned(device.devEui, r.port);
  return (
    <div className="tile-wrap">
      <button className={`tile reading ${r.onOff && r.value ? "on" : ""}`} aria-pressed={selected} onClick={onPick}>
        <ReadingWidget device={device} r={r} widget={widget} cfg={cfg} />
        <div className="lbl" title={r.label}>{r.label}</div>
        {widget === "onoff" ? (
          <span className={`w-onoff ${r.value ? "on" : ""}`}>{r.value ? "On" : "Off"}</span>
        ) : widget === "light" ? (
          <StatusLight on={r.value !== 0} label={r.label} bad={/trip|fault|alarm|fail/i.test(r.label)} />
        ) : widget === "flow" ? (
          <FlowToday device={device} r={r} />
        ) : widget === "rain" ? (
          <RainToday device={device} r={r} />
        ) : (
          <div className="big num">
            {f.value}
            {f.unit && <small>{f.unit}</small>}
          </div>
        )}
        <div className="foot" style={stale ? { color: "var(--warn)", fontWeight: 600 } : undefined}>
          {ago(r.at, now)} ·{" "}
          {footer ?? (readsDistance(cfg) ? `sensor reads ${formatNumber(r.value)} ${r.unit ?? ""} to the water` : <span className="mono">{r.port}</span>)}
        </div>
      </button>
      <button
        className={`pin-btn ${pinned ? "on" : ""}`}
        aria-pressed={pinned}
        aria-label={pinned ? `Take ${r.label} off your home screen` : `Pin ${r.label} to your home screen`}
        title={pinned ? "On your home screen (tap to remove)" : "Pin to your home screen"}
        onClick={() => home.toggle(device.devEui, r.port)}
      >
        <Icon name="pin" size={16} />
      </button>
    </div>
  );
}

/** The picture above a reading's value, as chosen in set-up (or picked from what the port is). */
function ReadingWidget({ device, r, widget, cfg }: { device: Device; r: ShownReading; widget: Widget; cfg: Device["ports"][number] | undefined }) {
  const level = tankLevel(cfg, r.value);
  if (widget === "tank") {
    const pct = tankPercent(cfg, r.value);
    return pct === undefined ? null : <Tank percent={pct} label={r.label} />;
  }
  if (widget === "wind") return <WindDial degrees={r.value} size={64} />;
  if (widget === "gauge" || widget === "thermo") {
    const { min, max } = widgetRange(cfg, widget, level);
    return widget === "gauge" ? <Gauge value={level} min={min} max={max} label={r.label} /> : <Thermometer value={level} min={min} max={max} label={r.label} />;
  }
  if (widget === "soil") return <SoilTile device={device} r={r} />;
  if (widget === "rose") return <WindRoseTile device={device} r={r} />;
  if (widget === "graph") return <TileSparkline devEui={device.devEui} port={r.port} label={r.label} cfg={cfg} />;
  return null;
}

/** Flow meter: today's total (the rise in the running total since local midnight) and the rate now. */
function FlowToday({ device, r }: { device: Device; r: ShownReading }) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const to = Math.floor(Date.now() / 300_000) * 300_000;
  const h = useLoad(() => api.readings(device.devEui, r.port, start.getTime() - HOUR, to), [device.devEui, r.port, to], undefined, { key: `${device.devEui}:${r.port}` });
  const pts = [...(h.data?.readings ?? [])].sort((a, b) => a.at - b.at);
  const first = pts.find((p) => p.at >= start.getTime()) ?? pts[0];
  // Litres today; counts are in L when the meter is set up. Anything else (mm of rain, kWh)
  // is shown in its own unit, not turned into litres.
  const water = !r.unit || /^(L|kL|ML|m³|m3|pulses)$/i.test(r.unit);
  if (!water) {
    const today = first ? Math.max(0, r.value - first.value) : 0;
    return (
      <div className="big num">
        {formatNumber(Math.round(today * 100) / 100)}
        <small>{r.unit}</small>
        <span className="muted small" style={{ marginLeft: 6 }}>today</span>
      </div>
    );
  }
  const litres = r.unit === "kL" ? 1000 : r.unit === "ML" ? 1e6 : 1;
  const today = first ? Math.max(0, r.value - first.value) * litres : 0;
  const last = pts.at(-2);
  const rate = last && r.at > last.at ? ((r.value - last.value) * litres) / ((r.at - last.at) / 1000) : undefined;
  const kl = today / 1000;
  return (
    <>
      <FlowMeter today={kl} {...(rate !== undefined && rate >= 0 ? { rate } : {})} />
      <div className="big num">
        {formatNumber(kl >= 1 ? kl : today)}
        <small>{kl >= 1 ? "kL" : "L"}</small>
        <span className="muted small" style={{ marginLeft: 6 }}>today</span>
      </div>
    </>
  );
}

/** "3.2 mm" fallen since local midnight, for the farm home's weather card. */
export function RainTodayValue({ device, port }: { device: Device; port: string }) {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const to = Math.floor(Date.now() / 300_000) * 300_000;
  const h = useLoad(() => api.readings(device.devEui, port, midnight.getTime() - 3 * HOUR, to), [device.devEui, port, to], undefined, { key: `${device.devEui}:${port}` });
  const pts = [...(h.data?.readings ?? [])].sort((a, b) => a.at - b.at);
  const unit = pts.at(-1)?.unit ?? "mm";
  return (
    <>
      <b>{formatNumber(Math.round(riseSince(pts, midnight.getTime()) * 10) / 10)}</b> <small>{unit.toLowerCase() === "mm" ? "mm" : unit}</small>
    </>
  );
}

/**
 * Rain gauge: what fell today (since local midnight), in the last 24 h and over the week, from
 * the gauge's running total, with the rate now when the station reports one. The running total
 * itself means little to a farmer; it's shown small underneath.
 */
function RainToday({ device, r }: { device: Device; r: ShownReading }) {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const to = Math.floor(Date.now() / 300_000) * 300_000;
  const from = to - 7 * 24 * HOUR;
  const h = useLoad(() => portHistory(device, r.port, from - HOUR, to, { counter: true }), [device.devEui, r.port, to], undefined, { key: `${device.devEui}:${r.port}` });
  const latest = useLoad(() => api.latest(device.devEui), [device.devEui], 60_000);
  const ratePort = rainRatePort(device, r.port, (latest.data?.readings ?? []).map((x) => x.port));
  const rate = ratePort ? latest.data?.readings.find((x) => x.port === ratePort) : undefined;
  const pts = [...(h.data?.points ?? []), r].sort((a, b) => a.at - b.at);
  const unit = (r.unit ?? "mm").toLowerCase() === "mm" ? "mm" : (r.unit ?? "mm");
  const today = riseSince(pts, midnight.getTime());
  const day = riseSince(pts, Date.now() - 24 * HOUR);
  const week = riseSince(pts, from);
  // A bar for each of the last 7 days, today on the right.
  const days = Array.from({ length: 7 }, (_, i) => {
    const start = new Date(midnight.getTime() - (6 - i) * 24 * HOUR).getTime();
    const end = start + 24 * HOUR;
    return riseSince(pts.filter((p) => p.at < end), start);
  });
  const top = Math.max(1, ...days);
  return (
    <>
      <div className="rain-bars" aria-hidden="true">
        {days.map((v, i) => (
          <span key={i} style={{ height: `${Math.max(v > 0 ? 8 : 2, (100 * v) / top)}%` }} className={i === 6 ? "today" : ""} title={`${formatNumber(v)} ${unit}`} />
        ))}
      </div>
      <div className="big num">
        {formatNumber(Math.round(today * 10) / 10)}
        <small>{unit}</small>
        <span className="muted small" style={{ marginLeft: 6 }}>today</span>
      </div>
      <div className="foot">
        24 h {formatNumber(Math.round(day * 10) / 10)} {unit} · 7 days {formatNumber(Math.round(week * 10) / 10)} {unit}
        {rate ? ` · now ${formatNumber(rate.value)} ${rate.unit ?? "mm/h"}` : ""}
      </div>
    </>
  );
}

/** A soil moisture tile: today's value against the refill and full points (the whole graph is in SoilCard). */
function SoilTile({ device, r }: { device: Device; r: ShownReading }) {
  const band = soilBand(device, r.port);
  return <MoistureBar value={r.value} refill={band.refill} full={band.full} />;
}

/**
 * Soil moisture, last 14 days, as in the prototype: a line for each depth on the probe, the
 * band between the refill and full points, and a sentence on when it'll need water.
 */
export function SoilCard({ device, ports, days = 14, title }: { device: Device; ports: string[]; days?: number; title?: string }) {
  const to = Math.floor(Date.now() / 600_000) * 600_000;
  const from = to - days * 24 * HOUR;
  const data = useLoad(
    () => Promise.all(ports.map((p) => portHistory(device, p, from, to).then((h) => ({ port: p, points: h.points })))),
    [device.devEui, ports.join(","), to],
    10 * 60_000,
    { key: `${device.devEui}:${ports.join(",")}:${days}` },
  );
  if (data.error && !data.data) return <Problem error={data.error} onRetry={data.reload} />;
  if (!data.data) return <div className="card soil-card"><Spinner /></div>;
  // The band and the sentence go by the root zone: the middle depth (or the only one).
  const root = data.data[Math.floor((data.data.length - 1) / 2)]!;
  const band = soilBand(device, root.port);
  // Points set on any depth count for the probe (most farms set them once).
  const set = band.set ? band : (ports.map((p) => soilBand(device, p)).find((b) => b.set) ?? band);
  const last = root.points[root.points.length - 1];
  const unit = last?.unit ?? "%";
  const name = (p: string) => portLabel(device, p).replace(/^(soil\s*)?moisture\s*/i, "") || portLabel(device, p);
  return (
    <div className="card soil-card">
      {title && (
        <div>
          <b>{title}</b>
          {last && <span className="muted small"> · {ago(last.at, Date.now())}</span>}
        </div>
      )}
      {last ? (
        <SoilChart series={data.data.map((s) => ({ label: data.data!.length > 1 ? name(s.port) : "", points: s.points }))} refill={set.refill} full={set.full} from={from} to={to} unit={unit === "%" ? "%" : ` ${unit}`} />
      ) : (
        <div className="muted small">No soil readings in the last {days} days.</div>
      )}
      <div className="legend">
        {data.data.map((s, j) => {
          const v = s.points[s.points.length - 1];
          return (
            <span key={s.port}>
              <i style={{ background: SOIL_COLOURS[j % SOIL_COLOURS.length] }} />
              {data.data!.length > 1 ? name(s.port) : portLabel(device, s.port)} <b className="num" style={{ color: "var(--ink)" }}>{v ? `${formatNumber(v.value)} ${v.unit ?? "%"}` : "–"}</b>
            </span>
          );
        })}
        <span>
          <i style={{ background: "var(--good-soft)", border: "1px solid var(--good)" }} />
          Refill {set.refill} – full {set.full} {unit}
        </span>
      </div>
      {last && <div className="small">{soilSummary(last.value, set, daysToRefill(root.points, set.refill, Date.now()), unit)}</div>}
      {!set.set && <div className="muted small">Refill and full points aren't set for this probe yet (showing 25–40 %). Set them in Set up → the moisture reading → Show as.</div>}
    </div>
  );
}

/** Wind rose from the last day of direction and speed readings, with now in the middle. */
function WindRoseTile({ device, r }: { device: Device; r: ShownReading }) {
  return <WindRoseFor device={device} direction={r.port} nowDir={r.value} />;
}

/** The wind rose for a weather station: its direction port and the speed beside it (Modbus stations too). */
export function WindRoseFor({ device, direction, nowDir, size }: { device: Device; direction: string; nowDir?: number; size?: number }) {
  const to = Math.floor(Date.now() / 600_000) * 600_000;
  const from = to - 24 * HOUR;
  const latest = useLoad(() => api.latest(device.devEui), [device.devEui], 60_000);
  const speedPort = windPorts(device, (latest.data?.readings ?? []).map((x) => x.port)).speed ?? "wind_speed";
  const dirs = useLoad(() => api.readings(device.devEui, direction, from, to), [device.devEui, direction, to], undefined, { key: `${device.devEui}:${direction}` });
  const speeds = useLoad(() => api.readings(device.devEui, speedPort, from, to), [device.devEui, speedPort, to], undefined, { key: `${device.devEui}:${speedPort}` });
  const d = [...(dirs.data?.readings ?? [])].sort((a, b) => a.at - b.at);
  // Direction and speed come from the same report, so they're paired by the minute.
  const byTime = new Map((speeds.data?.readings ?? []).map((x) => [Math.round(x.at / 60_000), x.value]));
  const sp = d.map((x) => byTime.get(Math.round(x.at / 60_000)) ?? 0);
  const nowSpeed = latest.data?.readings.find((x) => x.port === speedPort);
  const dirNow = nowDir ?? latest.data?.readings.find((x) => x.port === direction)?.value;
  return (
    <WindRose
      dirs={d.map((x) => x.value)}
      speeds={sp}
      {...(dirNow !== undefined ? { nowDir: dirNow } : {})}
      {...(nowSpeed ? { nowSpeed: nowSpeed.value, unit: nowSpeed.unit ?? "km/h" } : {})}
      {...(size ? { size } : {})}
    />
  );
}

function TileSparkline({ devEui, port, label, cfg }: { devEui: string; port: string; label: string; cfg: Device["ports"][number] | undefined }) {
  // Hourly-ish points over the last day; refreshed every 10 minutes.
  const to = Math.floor(Date.now() / 600_000) * 600_000;
  const h = useLoad(() => api.readings(devEui, port, to - 24 * HOUR, to), [devEui, port, to], undefined, { key: `${devEui}:${port}` });
  const pts = [...(h.data?.readings ?? [])].sort((a, b) => a.at - b.at).map((p) => tankLevel(cfg, p.value));
  return <Sparkline values={pts} label={label} />;
}

function ChartCard({ device, reading, now }: { device: Device; reading: ShownReading; now: number }) {
  // A device that's been silent a while has nothing in the last 24 h: open on a range that shows its last values.
  const silent = now - (device.lastSeenAt ?? now);
  const [range, setRange] = useState<(typeof RANGES)[number]["id"]>(silent > 7 * 24 * HOUR ? "30d" : silent > 20 * HOUR ? "7d" : "24h");
  const ms = RANGES.find((r) => r.id === range)!.ms;
  // Round "to" to the minute so the 30 s clock tick doesn't refetch the history every time.
  const to = Math.ceil(now / 60_000) * 60_000;
  const from = to - ms;
  const series = useLoad(() => portHistory(device, reading.port, from, to), [device.devEui, reading.port, range, Math.floor(to / 300_000)], undefined, {
    key: `${device.devEui}:${reading.port}:${range}`,
  });
  // Ultrasonic tank sensors: chart the water level, as the tiles show it.
  const cfg = device.ports.find((p) => portKey(p) === reading.port);
  const points = (series.data?.points ?? []).map((p) => (readsDistance(cfg) ? { ...p, value: tankLevel(cfg, p.value) } : p));
  // On/off history from /chart is only the changes: carry the last state on to when the device was last heard.
  const changes = series.data?.detail === "changes";
  const lastPoint = points.at(-1);
  if (changes && lastPoint) {
    const end = Math.min(to, device.lastSeenAt ?? to);
    if (end > lastPoint.at) points.push({ at: end, value: lastPoint.value });
  }
  const unit = reading.unit ?? "";
  const vals = points.map((p) => p.value);

  return (
    <section className="card chart-card">
      <div className="chart-top">
        <h3>{reading.label}</h3>
        {points.length > 0 && !reading.onOff && (
          <span className="muted small num">
            min {formatReadingValue(Math.min(...vals), unit)} · max {formatReadingValue(Math.max(...vals), unit)}
          </span>
        )}
        <div className="chips" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button key={r.id} className="chip" aria-pressed={range === r.id} onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {series.error ? <Problem error={series.error} onRetry={series.reload} /> : null}
      {!series.data && !series.error && <Spinner />}
      {series.data && !points.length && <div className="empty">No readings in this period.</div>}
      {points.length > 0 && <LineChart points={points} unit={unit} onOff={reading.onOff} from={from} to={to} gaps={!changes} />}
      <div className="toolbar">
        {series.data?.detail === "raw" && points.length >= 5000 && <span className="muted small">Showing the newest 5,000 readings in this period.</span>}
        {series.data?.detail === "hour" && <span className="muted small">Hourly averages over this period.</span>}
        {points.length > 0 && (
          <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={() => downloadCsv(device, reading, points)}>
            Download CSV
          </button>
        )}
      </div>
    </section>
  );
}

const formatReadingValue = (v: number, unit: string) => (unit ? `${formatNumber(v)} ${unit}` : formatNumber(v));

/** Saves what the chart shows as a spreadsheet-friendly file (local time and UTC). */
function downloadCsv(device: Device, reading: ShownReading, points: Array<{ at: number; value: number; raw?: number }>) {
  const rows = [...points].sort((a, b) => a.at - b.at);
  const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [
    ["Local time", "UTC", `${reading.label}${reading.unit ? ` (${reading.unit})` : ""}`, "Raw"].map(esc).join(","),
    ...rows.map((p) =>
      [new Date(p.at).toLocaleString("en-AU", { hour12: false }), new Date(p.at).toISOString(), String(p.value), p.raw === undefined ? "" : String(p.raw)].map(esc).join(","),
    ),
  ];
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${device.name} - ${reading.label} - ${new Date().toISOString().slice(0, 10)}.csv`.replace(/[\\/:*?"<>|]/g, "-");
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** What looks different from normal on this device (nothing shown when all is as usual). */
/** The farm's name for a gateway, with its EUI; just the EUI until it's named. */
function GatewayName({ eui, accountId }: { eui: string; accountId: string }) {
  const gws = useLoad(() => api.gateways(accountId).catch(() => ({ gateways: [] })), [accountId], 10 * 60_000);
  const name = gws.data?.gateways.find((g) => g.eui === eui)?.name;
  return name ? (
    <>
      {name} <span className="mono muted small">{formatEui(eui)}</span>
    </>
  ) : (
    <span className="mono">{formatEui(eui)}</span>
  );
}

function DeviceInsights({ devEui, accountId }: { devEui: string; accountId: string }) {
  const insights = useLoad(() => api.insights(accountId), [accountId], 10 * 60_000);
  const mine = (insights.data?.insights ?? []).filter((i) => i.devEui === devEui);
  if (!mine.length) return null;
  return (
    <Section title="Insights">
      <InsightList insights={mine} devEui={devEui} />
    </Section>
  );
}
